import {Easing} from '@config/easing';
import {Collapse, CollapseClasses} from '@mui/material';
import {EmptyObject} from '@utils/typescript';
import classNames from 'classnames';
import {ReactNode, useId} from 'react';
import styles from './MeinAccordionItem.module.css';
import {useAccordionGroup} from './useAccordionGroup';

type IProps<K extends string | number | null, TogglerProps, ContentProps> = {
    toggler: (props: IAccordionGroupItemProps & TogglerProps) => ReactNode;
    content: (props: IAccordionGroupItemProps & ContentProps) => ReactNode;
    id?: K;
    className?: string;
    innerClassname?: string;
    togglerClassName?: string;
    contentClassName?: string;
    collapseClasses?: Partial<CollapseClasses>;
    separatorType?: 'none' | 'dynamic' | 'static';
    noCloseSelf?: boolean;
    unmountOnExit?: boolean;
    onOpen?: () => void;
} & (TogglerProps extends EmptyObject | undefined
    ? {togglerProps?: undefined}
    : {togglerProps: Omit<TogglerProps, keyof IAccordionGroupItemProps>}) &
    (ContentProps extends EmptyObject | undefined
        ? {contentProps?: undefined}
        : {contentProps: Omit<ContentProps, keyof IAccordionGroupItemProps>});

export interface IAccordionGroupItemProps {
    toggle: () => void;
    isOpen: boolean;
}

export const MeinAccordionItem = <
    K extends string | number | null,
    TogglerProps extends object | undefined,
    ContentProps extends object | undefined,
>({
    toggler: Toggler,
    content: Content,
    // eslint-disable-next-line react-hooks/rules-of-hooks
    id = useId() as K,
    className,
    innerClassname,
    contentClassName,
    togglerClassName,
    separatorType = 'none',
    noCloseSelf = false,
    unmountOnExit,
    onOpen,
    togglerProps,
    contentProps,
    collapseClasses,
    ...rest
}: IProps<K, TogglerProps, ContentProps>) => {
    const {current, onSelect, shouldGroupUnmountOnExit} = useAccordionGroup();
    const isOpen = current === id;

    const toggle = () => {
        onSelect(isOpen ? null : id);
    };

    const handleClick = () => {
        if (isOpen && noCloseSelf) {
            return;
        }
        if (!isOpen) {
            onOpen?.();
        }
        toggle();
    };

    const props = {
        isOpen,
        toggle,
    };

    return (
        <div
            className={classNames(
                {
                    [styles.border]:
                        separatorType === 'static' ||
                        (separatorType === 'dynamic' && !isOpen),
                },
                styles.container,
                className,
            )}
            data-open={isOpen}
            {...rest}
        >
            <div className={innerClassname}>
                <div className={togglerClassName} onClick={handleClick}>
                    <Toggler
                        {...({...props, ...togglerProps} as TogglerProps &
                            IAccordionGroupItemProps)}
                    />
                </div>
                <Collapse
                    classes={collapseClasses}
                    easing={`cubic-bezier(${Easing.bezier.outNormal.join(
                        ',',
                    )})`}
                    in={isOpen}
                    collapsedSize={0}
                    unmountOnExit={
                        unmountOnExit !== undefined
                            ? unmountOnExit
                            : shouldGroupUnmountOnExit
                    }
                >
                    <div className={contentClassName}>
                        <Content
                            {...({...props, ...contentProps} as ContentProps &
                                IAccordionGroupItemProps)}
                        />
                    </div>
                </Collapse>
            </div>
        </div>
    );
};
